import {
  FETCH_EAT_RESTAURANTS,
  FETCH_EAT_RESTAURANTS_SUCCESS,
  FETCH_EAT_RESTAURANTS_FAILED,
  UPDATE_EAT_RESTAURANT,
  UPDATE_EAT_RESTAURANT_SUCCESS,
  UPDATE_EAT_RESTAURANT_FAILED,
  FETCH_EAT_MENU,
  FETCH_EAT_MENU_SUCCESS,
  FETCH_EAT_MENU_FAILED,
  UPDATE_EAT_MENU,
  UPDATE_EAT_MENU_SUCCESS,
  UPDATE_EAT_MENU_FAILED,
  FETCH_EAT_SETTINGS,
  FETCH_EAT_SETTINGS_SUCCESS,
  FETCH_EAT_SETTINGS_FAILED,
  FETCH_EAT_ORDERS,
  FETCH_EAT_ORDERS_SUCCESS,
  FETCH_EAT_ORDERS_FAILED,
  FETCH_EAT_COURIERS,
  FETCH_EAT_COURIERS_SUCCESS,
  FETCH_EAT_COURIERS_FAILED,
  FETCH_EAT_PROMOS,
  FETCH_EAT_PROMOS_SUCCESS,
  FETCH_EAT_PROMOS_FAILED,
  EDIT_EAT_PROMOS,
  CREATE_EAT_ORDER,
  CREATE_EAT_ORDER_SUCCESS,
  CREATE_EAT_ORDER_FAILED,
  UPDATE_EAT_ORDER,
  UPDATE_EAT_ORDER_SUCCESS,
  UPDATE_EAT_ORDER_FAILED,
  UPDATE_EAT_COURIER,
  UPDATE_EAT_COURIER_SUCCESS,
  UPDATE_EAT_COURIER_FAILED,
  SET_EAT_CART,
  ADD_EAT_CART_ITEM,
  UPDATE_EAT_CART_ITEM_QTY,
  REMOVE_EAT_CART_ITEM,
  CLEAR_EAT_CART,
  CLEAR_EAT_MESSAGES
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { get, onValue, push, remove, runTransaction, set, update } from "firebase/database";
import { GetDistance } from '../other/GeoFunctions';
import { RequestPushMsg } from '../other/NotificationFunctions';

const snapshotToArray = (snapshot) => {
  const data = snapshot.val();
  if (!data) return [];
  return Object.keys(data).map((key) => ({ ...data[key], id: data[key].id || key }));
};

const makeOrderReference = () => {
  const suffix = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `EAT-${Date.now().toString().slice(-6)}-${suffix}`;
};

// Code 4 chiffres pour valider physiquement le retrait restaurant et la remise client.
// Empeche les abus: le restaurant communique le pickupCode au livreur, le client communique
// le deliveryCode au livreur. Le livreur doit saisir le bon code pour avancer le statut.
const makeFourDigitCode = () => String(Math.floor(1000 + Math.random() * 9000));
const normalizeCode = (value) => String(value == null ? '' : value).replace(/\D/g, '').slice(0, 4);

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const emitWalletTransferNotification = async (uid, direction, amount, description, params = {}) => {
  const { singleUserRef, userNotificationsRef } = firebase;
  if (!uid || !singleUserRef || !userNotificationsRef || !amount) return;
  const snapshot = await get(singleUserRef(uid));
  const user = snapshot && snapshot.exists() ? snapshot.val() : null;
  const formattedAmount = Number(amount).toFixed(2);
  const msg = `${direction === 'in' ? 'Fonds entrants' : 'Fonds sortants'}: ${direction === 'in' ? '+' : '-'}${formattedAmount} - ${description || 'Allô Driver'}`;
  const notification = {
    title: 'Wallet',
    msg,
    screen: 'Wallet',
    params: { ...params, direction, amount: Number(formattedAmount) },
    createdAt: Date.now(),
    read: false,
    type: 'wallet_transfer'
  };
  await push(userNotificationsRef(uid), notification);
  if (user && user.pushToken) {
    RequestPushMsg(user.pushToken, notification);
  }
};

const getLocationPoint = (value) => {
  if (!value) return null;
  const source = value.coords || value.location || value;
  if (!source) return null;
  const lat = toNumber(source.lat ?? source.latitude, NaN);
  const lng = toNumber(source.lng ?? source.longitude, NaN);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const getCourierName = (courier = {}) => {
  return `${courier.firstName || ''} ${courier.lastName || ''}`.trim() || courier.name || courier.uid || courier.id || 'Livreur Allô Driver Eats';
};

const getEatDeliveryLimits = (settings = {}, courier = {}, order = {}) => {
  const maxDeliveryDistanceKm = toNumber(
    courier.maxDeliveryDistanceKm ||
    courier.eatMaxDeliveryDistanceKm ||
    order.maxCourierDistanceKm ||
    settings.courierMaxDeliveryDistanceKm ||
    settings.eatMaxDeliveryDistanceKm ||
    settings.maxEatDeliveryDistanceKm,
    6
  );
  const maxTotalRouteKm = toNumber(
    courier.maxTotalRouteKm ||
    courier.eatMaxTotalRouteKm ||
    settings.courierMaxTotalRouteKm ||
    settings.eatMaxCourierRouteKm ||
    settings.maxEatCourierRouteKm,
    12
  );

  return { maxDeliveryDistanceKm, maxTotalRouteKm };
};

export const addEatCartItem = (restaurant, item) => ({
  type: ADD_EAT_CART_ITEM,
  payload: { restaurant, item }
});

export const updateEatCartItemQty = (cartKey, qty) => ({
  type: UPDATE_EAT_CART_ITEM_QTY,
  payload: { cartKey, qty }
});

export const removeEatCartItem = (cartKey) => ({
  type: REMOVE_EAT_CART_ITEM,
  payload: { cartKey }
});

export const setEatCart = (cart) => ({
  type: SET_EAT_CART,
  payload: cart || { restaurant: null, items: [] }
});

export const clearEatCart = () => ({ type: CLEAR_EAT_CART });

const getOrderDeliveryDistance = (order = {}) => {
  const restaurantPoint = getLocationPoint(order.restaurantLocation || order.restaurant);
  const deliveryPoint = getLocationPoint(order.delivery && order.delivery.location ? order.delivery.location : order.delivery);
  if (!restaurantPoint || !deliveryPoint) return null;
  return GetDistance(restaurantPoint.lat, restaurantPoint.lng, deliveryPoint.lat, deliveryPoint.lng);
};

const getCourierScore = (courier, restaurantPoint, deliveryPoint, settings = {}, order = {}) => {
  const courierPoint = getLocationPoint(courier);
  if (!courierPoint || !restaurantPoint) return null;

  const pickupDistance = GetDistance(courierPoint.lat, courierPoint.lng, restaurantPoint.lat, restaurantPoint.lng);
  const deliveryDistance = deliveryPoint ? GetDistance(restaurantPoint.lat, restaurantPoint.lng, deliveryPoint.lat, deliveryPoint.lng) : 0;
  const maxPickupRadius = toNumber(courier.pickupRadiusKm || settings.courierPickupRadiusKm || settings.courierRadiusKm, 8);
  const { maxDeliveryDistanceKm, maxTotalRouteKm } = getEatDeliveryLimits(settings, courier, order);
  const isAvailable = courier.active !== false && courier.available !== false && courier.busy !== true && courier.status !== 'OFFLINE';

  if (!isAvailable || pickupDistance > maxPickupRadius || deliveryDistance > maxDeliveryDistanceKm || pickupDistance + deliveryDistance > maxTotalRouteKm) return null;

  const rating = Math.min(toNumber(courier.rating, 4.5), 5);
  const capacityBonus = courier.vehicleType === 'bike' || courier.transportType === 'bike' || courier.deliveryMode === 'bike' ? 0.4 : 0;
  const score = pickupDistance + deliveryDistance * 0.25 - rating * 0.15 - capacityBonus;

  return {
    score,
    pickupDistance: Number(pickupDistance.toFixed(2)),
    deliveryDistance: Number(deliveryDistance.toFixed(2)),
    totalRouteDistance: Number((pickupDistance + deliveryDistance).toFixed(2)),
    maxDeliveryDistanceKm,
    maxTotalRouteKm
  };
};

const findBestEatCourier = async (order, settings = {}, excludedCourierIds = []) => {
  const { eatCouriersRef } = firebase;
  if (!eatCouriersRef) return null;
  const excluded = new Set(excludedCourierIds.filter(Boolean));

  const restaurantPoint = getLocationPoint(order.restaurantLocation || order.restaurant);
  const deliveryPoint = getLocationPoint(order.delivery && order.delivery.location ? order.delivery.location : order.delivery);
  if (!restaurantPoint) return null;

  const snapshot = await get(eatCouriersRef);
  const couriers = snapshotToArray(snapshot);
  const candidates = couriers
    .filter(courier => courier && !excluded.has(courier.uid || courier.id) && !courier.currentOrderId && !courier.currentOfferId && courier.active && courier.approved && !['documents_required', 'pending_review', 'rejected'].includes(courier.applicationStatus))
    .map(courier => {
      const distances = getCourierScore(courier, restaurantPoint, deliveryPoint, settings, order);
      return distances ? { ...courier, ...distances } : null;
    })
    .filter(Boolean)
    .sort((leftCourier, rightCourier) => leftCourier.score - rightCourier.score);

  return candidates[0] || null;
};

const buildEatOrderTimelinePatch = (status, now) => {
  const keyByStatus = {
    PLACED: 'placedAt',
    PAID: 'paidAt',
    RESTAURANT_ACCEPTED: 'restaurantAcceptedAt',
    PREPARING: 'preparingAt',
    READY_FOR_PICKUP: 'readyForPickupAt',
    PICKED_UP: 'pickedUpAt',
    DELIVERED: 'deliveredAt',
    CANCELLED: 'cancelledAt'
  };
  return keyByStatus[status] ? { [keyByStatus[status]]: now } : {};
};

export const fetchEatSettings = () => (dispatch) => {
  const { eatSettingsRef } = firebase;

  if (!eatSettingsRef) {
    dispatch({ type: FETCH_EAT_SETTINGS_FAILED, payload: 'Database not available' });
    return;
  }

  dispatch({ type: FETCH_EAT_SETTINGS, payload: null });
  onValue(eatSettingsRef, snapshot => {
    dispatch({
      type: FETCH_EAT_SETTINGS_SUCCESS,
      payload: snapshot.val() || {}
    });
  });
};

export const fetchEatRestaurants = () => (dispatch) => {
  const { eatRestaurantsRef } = firebase;

  if (!eatRestaurantsRef) {
    dispatch({ type: FETCH_EAT_RESTAURANTS_FAILED, payload: 'Database not available' });
    return;
  }

  dispatch({ type: FETCH_EAT_RESTAURANTS, payload: null });
  onValue(eatRestaurantsRef, snapshot => {
    const restaurants = snapshotToArray(snapshot);
    dispatch({
      type: restaurants.length ? FETCH_EAT_RESTAURANTS_SUCCESS : FETCH_EAT_RESTAURANTS_FAILED,
      payload: restaurants.length ? restaurants : 'No Eat restaurants available.'
    });
  });
};

export const updateEatRestaurant = (restaurantId, patch = {}) => async (dispatch) => {
  const { eatRestaurantRef } = firebase;

  if (!restaurantId || !eatRestaurantRef) {
    dispatch({ type: UPDATE_EAT_RESTAURANT_FAILED, payload: 'Invalid Eat restaurant' });
    return;
  }

  dispatch({ type: UPDATE_EAT_RESTAURANT, payload: { restaurantId, patch } });

  try {
    const now = Date.now();
    const updateData = {
      ...patch,
      id: restaurantId,
      updatedAt: now
    };
    await update(eatRestaurantRef(restaurantId), updateData);
    dispatch({ type: UPDATE_EAT_RESTAURANT_SUCCESS, payload: updateData });
  } catch (error) {
    dispatch({ type: UPDATE_EAT_RESTAURANT_FAILED, payload: error.message || String(error) });
  }
};

export const fetchEatMenu = (restaurantId) => (dispatch) => {
  const { eatRestaurantMenuRef } = firebase;

  if (!restaurantId || !eatRestaurantMenuRef) {
    dispatch({ type: FETCH_EAT_MENU_FAILED, payload: 'Invalid Eat restaurant menu' });
    return;
  }

  dispatch({ type: FETCH_EAT_MENU, payload: restaurantId });
  onValue(eatRestaurantMenuRef(restaurantId), snapshot => {
    dispatch({
      type: FETCH_EAT_MENU_SUCCESS,
      payload: {
        restaurantId,
        menu: snapshot.val() || { categories: {}, items: {} }
      }
    });
  });
};

export const updateEatMenu = (restaurantId, patch = {}) => async (dispatch) => {
  const { eatRestaurantMenuRef } = firebase;

  if (!restaurantId || !eatRestaurantMenuRef) {
    dispatch({ type: UPDATE_EAT_MENU_FAILED, payload: 'Invalid Eat restaurant menu' });
    return;
  }

  dispatch({ type: UPDATE_EAT_MENU, payload: { restaurantId, patch } });

  try {
    await update(eatRestaurantMenuRef(restaurantId), patch);
    dispatch({ type: UPDATE_EAT_MENU_SUCCESS, payload: { restaurantId, patch } });
  } catch (error) {
    dispatch({ type: UPDATE_EAT_MENU_FAILED, payload: error.message || String(error) });
    throw error;
  }
};

export const fetchEatCouriers = () => (dispatch) => {
  // Système parallèle VTC : on croise eat/couriers avec /locations/ (même chemin GPS que VTC)
  // Exactement comme fetchDrivers croise /drivers/ avec /locations/
  const { eatCouriersRef, allLocationsRef } = firebase;

  if (!eatCouriersRef) {
    dispatch({ type: FETCH_EAT_COURIERS_FAILED, payload: 'Database not available' });
    return;
  }

  dispatch({ type: FETCH_EAT_COURIERS, payload: null });
  onValue(eatCouriersRef, snapshot => {
    const rawCouriers = snapshotToArray(snapshot);
    if (!allLocationsRef) {
      // Fallback si allLocationsRef absent
      dispatch({
        type: rawCouriers.length ? FETCH_EAT_COURIERS_SUCCESS : FETCH_EAT_COURIERS_FAILED,
        payload: rawCouriers.length ? rawCouriers : 'No Eat couriers available.'
      });
      return;
    }
    // Même pattern que fetchDrivers : lire /locations/ et fusionner la position GPS fraîche
    onValue(allLocationsRef, locSnapshot => {
      const locations = locSnapshot.val() || {};
      const couriers = rawCouriers.map(courier => {
        const uid = courier.uid || courier.id;
        const locData = uid && locations[uid];
        if (locData && locData.lat && locData.lng) {
          // Fusion de la position depuis /locations/{uid} — identique à VTC
          return {
            ...courier,
            lat: locData.lat,
            lng: locData.lng,
            location: locData,
            lastLocationUpdate: locData.lastLocationUpdate || courier.lastLocationUpdate,
            locationUpdatedAt: locData.locationUpdatedAt || courier.locationUpdatedAt
          };
        }
        return courier;
      });
      dispatch({
        type: couriers.length ? FETCH_EAT_COURIERS_SUCCESS : FETCH_EAT_COURIERS_FAILED,
        payload: couriers.length ? couriers : 'No Eat couriers available.'
      });
    }, { onlyOnce: true });
  });
};

export const fetchEatPromos = () => (dispatch) => {
  const { eatPromosRef } = firebase;

  if (!eatPromosRef) {
    dispatch({ type: FETCH_EAT_PROMOS_FAILED, payload: 'Database not available' });
    return;
  }

  dispatch({ type: FETCH_EAT_PROMOS, payload: null });
  onValue(eatPromosRef, snapshot => {
    const promos = snapshotToArray(snapshot);
    dispatch({
      type: promos.length ? FETCH_EAT_PROMOS_SUCCESS : FETCH_EAT_PROMOS_FAILED,
      payload: promos.length ? promos : 'No Eat promos available.'
    });
  });
};

export const editEatPromo = (promo, method) => (dispatch) => {
  const { eatPromosRef, eatPromoEditRef } = firebase;

  if (!eatPromosRef || !eatPromoEditRef) {
    dispatch({ type: FETCH_EAT_PROMOS_FAILED, payload: 'Database not available' });
    return;
  }

  dispatch({ type: EDIT_EAT_PROMOS, payload: { method, promo } });
  if (method === 'Add') {
    push(eatPromosRef, { ...promo, service: 'eat' });
  } else if (method === 'Delete') {
    remove(eatPromoEditRef(promo.id));
  } else {
    set(eatPromoEditRef(promo.id), { ...promo, service: 'eat' });
  }
};

export const fetchEatOrders = (scope = {}) => (dispatch) => {
  const {
    eatOrdersRef,
    eatCustomerOrdersRef,
    eatRestaurantOrdersRef,
    eatCourierOrdersRef
  } = firebase;

  const orderRef = scope.availableForCourier && eatOrdersRef ? eatOrdersRef :
    scope.customerId && eatCustomerOrdersRef ? eatCustomerOrdersRef(scope.customerId) :
    scope.restaurantId && eatRestaurantOrdersRef ? eatRestaurantOrdersRef(scope.restaurantId) :
      scope.courierId && eatCourierOrdersRef ? eatCourierOrdersRef(scope.courierId) : eatOrdersRef;

  if (!orderRef) {
    dispatch({ type: FETCH_EAT_ORDERS_FAILED, payload: 'Database not available' });
    return;
  }

  if (!scope.silent) dispatch({ type: FETCH_EAT_ORDERS, payload: null });
  return onValue(orderRef, snapshot => {
    const courierPoint = getLocationPoint(scope.courierLocation);
    const orders = snapshotToArray(snapshot).filter(order => {
      if (!scope.availableForCourier) return true;
      if (['DELIVERED', 'CANCELLED'].includes(order.status)) return false;
      if (order.courierId && order.courierId !== scope.courierId) return false;
      if (!courierPoint) return true;

      const restaurantPoint = getLocationPoint(order.restaurantLocation || order.restaurant);
      if (!restaurantPoint) return true;
      const distance = GetDistance(courierPoint.lat, courierPoint.lng, restaurantPoint.lat, restaurantPoint.lng);
      const radius = toNumber(scope.radiusKm, 8);
      const deliveryDistance = toNumber(order.delivery && order.delivery.distanceKm ? order.delivery.distanceKm : order.pricing && order.pricing.deliveryDistanceKm, 0);
      const maxDeliveryDistanceKm = toNumber(scope.maxDeliveryDistanceKm, 6);
      const maxTotalRouteKm = toNumber(scope.maxTotalRouteKm, 12);
      return distance <= radius && deliveryDistance <= maxDeliveryDistanceKm && distance + deliveryDistance <= maxTotalRouteKm;
    }).sort((leftOrder, rightOrder) => {
      return (rightOrder.createdAt || 0) - (leftOrder.createdAt || 0);
    });
    dispatch({
      type: orders.length || scope.silent ? FETCH_EAT_ORDERS_SUCCESS : FETCH_EAT_ORDERS_FAILED,
      payload: orders.length || scope.silent ? orders : 'No Eat orders available.'
    });
  });
};

export const createEatOrder = (order) => async (dispatch) => {
  const {
    eatOrdersRef,
    eatOrderRef,
    eatSettingsRef,
    singleUserRef,
    walletHistoryRef
  } = firebase;

  if (!eatOrdersRef || !eatOrderRef) {
    const message = 'Service de commande indisponible pour le moment.';
    dispatch({ type: CREATE_EAT_ORDER_FAILED, payload: message });
    return { error: true, message };
  }

  const failCreateEatOrder = (message) => {
    dispatch({ type: CREATE_EAT_ORDER_FAILED, payload: message });
    return { error: true, message };
  };

  dispatch({ type: CREATE_EAT_ORDER, payload: order });

  try {
    const createdRef = push(eatOrdersRef);
    const now = Date.now();
    const orderId = createdRef.key;
    const paymentMethod = order.payment?.method || order.payment_mode || 'wallet';
    if (paymentMethod === 'cash' || order.payment_mode === 'cash') {
      return failCreateEatOrder('Le paiement en espèces n’est pas accepté pour Allô Driver Eats. Choisissez le wallet ou la carte bancaire.');
    }
    const settingsSnapshot = eatSettingsRef ? await get(eatSettingsRef) : null;
    const eatSettings = settingsSnapshot && settingsSnapshot.exists() ? settingsSnapshot.val() : {};
    const orderDeliveryDistance = getOrderDeliveryDistance(order);
    const { maxDeliveryDistanceKm } = getEatDeliveryLimits(eatSettings, {}, order);
    if (orderDeliveryDistance !== null && orderDeliveryDistance > maxDeliveryDistanceKm) {
      return failCreateEatOrder(`Adresse trop éloignée pour la livraison (${Number(orderDeliveryDistance.toFixed(1))} km / limite ${Number(maxDeliveryDistanceKm.toFixed(1))} km). Essayez une adresse plus proche.`);
    }
    const isPaymentPending = order.status === 'PAYMENT_PENDING' || order.payment?.status === 'PENDING';
    const assignedCourier = order.courierId || isPaymentPending ? null : await findBestEatCourier(order, eatSettings);
    if (!assignedCourier && !isPaymentPending) {
      return failCreateEatOrder('Aucun livreur disponible autour du restaurant pour le moment. Réessayez dans quelques minutes.');
    }
    const courierPatch = assignedCourier ? {
      courierId: assignedCourier.uid || assignedCourier.id,
      courierName: getCourierName(assignedCourier),
      courierPhone: assignedCourier.mobile || assignedCourier.phone || '',
      courierRating: assignedCourier.rating || null,
      courierPickupDistance: assignedCourier.pickupDistance,
      courierDeliveryDistance: assignedCourier.deliveryDistance,
      courierTotalRouteDistance: assignedCourier.totalRouteDistance,
      maxCourierDistanceKm: assignedCourier.maxDeliveryDistanceKm,
      maxCourierRouteKm: assignedCourier.maxTotalRouteKm,
      courierStatus: 'ASSIGNED',
      dispatchStatus: 'ASSIGNED',
      assignedAt: now,
      dispatchMode: 'AUTO_NEAREST'
    } : {
      courierStatus: 'SEARCHING',
      dispatchStatus: 'SEARCHING',
      dispatchMode: 'AUTO_NEAREST'
    };
    const mainData = {
      ...order,
      ...courierPatch,
      id: orderId,
      type: 'eat',
      reference: order.reference || makeOrderReference(),
      pickupCode: order.pickupCode || makeFourDigitCode(),
      deliveryCode: order.deliveryCode || makeFourDigitCode(),
      status: order.status || 'PLACED',
      payment_mode: paymentMethod,
      cashPaymentAmount: 0,
      payment: order.payment || { method: 'wallet', status: 'PAID', paidAt: now },
      createdAt: order.createdAt || now,
      updatedAt: now,
      placedAt: order.placedAt || now
    };
    const pricingSubtotal = toNumber(mainData.pricing?.subtotal, 0);
    const pricingDelivery = toNumber(mainData.pricing?.deliveryFee, 0);
    const pricingDiscount = Math.min(pricingSubtotal, Math.max(0, toNumber(mainData.pricing?.discount || mainData.discount, 0)));
    const safeTotal = Number(Math.max(0, pricingSubtotal + pricingDelivery - pricingDiscount).toFixed(2));
    mainData.discount = pricingDiscount;
    mainData.payableAmount = safeTotal;
    mainData.pricing = {
      ...(mainData.pricing || {}),
      discount: pricingDiscount,
      total: safeTotal,
      platformNet: Number(Math.max(0, toNumber(mainData.pricing?.platformNet, 0)).toFixed(2))
    };
    mainData.payment = {
      ...(mainData.payment || {}),
      amount: safeTotal
    };
    if (paymentMethod === 'card') {
      mainData.cardPaymentAmount = safeTotal;
      mainData.customer_paid = 0;
      mainData.usedWalletMoney = 0;
    } else if (paymentMethod === 'wallet') {
      mainData.cardPaymentAmount = 0;
      mainData.customer_paid = safeTotal;
      mainData.usedWalletMoney = safeTotal;
    }

    if (paymentMethod === 'wallet') {
      const debitAmount = toNumber(mainData.payableAmount || mainData.payment?.amount || mainData.pricing?.total, 0);
      if (!singleUserRef || !walletHistoryRef || !mainData.customerId || debitAmount <= 0) {
        return failCreateEatOrder('Wallet indisponible pour cette commande. Réessayez ou choisissez la carte bancaire si elle est proposée.');
      }
      let walletDebited = false;
      const transactionResult = await runTransaction(singleUserRef(mainData.customerId), (user) => {
        if (!user) return user;
        const currentBalance = toNumber(user.walletBalance, 0);
        if (currentBalance < debitAmount) return;
        walletDebited = true;
        return { ...user, walletBalance: Number((currentBalance - debitAmount).toFixed(2)) };
      });
      if (!transactionResult.committed || !walletDebited) {
        return failCreateEatOrder('Solde wallet insuffisant pour cette commande. Rechargez votre wallet ou choisissez la carte bancaire.');
      }
      await push(walletHistoryRef(mainData.customerId), {
        type: 'Debit',
        amount: Number(debitAmount.toFixed(2)),
        date: now,
        txRef: orderId,
        service: 'eat'
      });
      await emitWalletTransferNotification(
        mainData.customerId,
        'out',
        debitAmount,
        `Commande ${mainData.reference || orderId}`,
        { orderId, service: 'eat' }
      );
    }

    await set(eatOrderRef(orderId), mainData);

    dispatch({ type: CREATE_EAT_ORDER_SUCCESS, payload: mainData });
    return mainData;
  } catch (error) {
    return failCreateEatOrder(error.message || 'Commande impossible pour le moment. Réessayez dans quelques minutes.');
  }
};

export const updateEatOrderStatus = (order, status, patch = {}) => async (dispatch) => {
  const {
    eatOrderRef,
    eatCustomerOrderRef,
    eatRestaurantOrderRef,
    eatCourierOrderRef,
    eatCourierRef,
    eatSettingsRef,
    singleUserRef,
    walletHistoryRef
  } = firebase;

  if (!order || !order.id || !eatOrderRef) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Invalid Eat order' });
    return;
  }

  dispatch({ type: UPDATE_EAT_ORDER, payload: { order, status, patch } });

  try {
    const now = Date.now();
    let currentOrder = order;
    if (['PICKED_UP', 'DELIVERED'].includes(status)) {
      const snapshot = await get(eatOrderRef(order.id));
      currentOrder = snapshot && snapshot.exists() ? snapshot.val() : order;
      if (!currentOrder || ['CANCELLED'].includes(currentOrder.status)) {
        dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'La commande n est plus disponible.' });
        return false;
      }
      if (currentOrder.status === status) {
        dispatch({ type: UPDATE_EAT_ORDER_SUCCESS, payload: { ...currentOrder, id: order.id } });
        return true;
      }
      // PICKED_UP : on accepte tous les statuts pré-retrait pour ne pas bloquer le livreur
      // si le restaurant oublie de cliquer "Prête au retrait". On comble le timeline manquant
      // automatiquement via buildEatOrderTimelinePatch + un patch additionnel ci-dessous.
      const pickupAllowedStatuses = [
        'PLACED', 'PAID',
        'RESTAURANT_ACCEPTED', 'PREPARING', 'READY_FOR_PICKUP',
        'COURIER_ASSIGNED', 'COURIER_ACCEPTED'
      ];
      if (status === 'PICKED_UP' && !pickupAllowedStatuses.includes(currentOrder.status)) {
        dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: `Retrait impossible: la commande est en statut ${currentOrder.status || 'inconnu'}.` });
        return false;
      }
      // Verification du code de retrait communique par le restaurateur au livreur.
      // Tolere les commandes anciennes sans pickupCode (compat retroactive) en autorisant
      // l'absence de code stocke; sinon, la correspondance stricte est requise.
      if (status === 'PICKED_UP' && currentOrder.pickupCode) {
        const expectedPickup = normalizeCode(currentOrder.pickupCode);
        const providedPickup = normalizeCode(patch && patch.providedPickupCode);
        if (!providedPickup || providedPickup !== expectedPickup) {
          dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Code de retrait incorrect. Demandez le code 4 chiffres au restaurant.' });
          return false;
        }
      }
      if (status === 'DELIVERED' && currentOrder.status !== 'PICKED_UP') {
        dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: `Livraison impossible: la commande est en statut ${currentOrder.status || 'inconnu'}, le retrait n a pas ete confirme.` });
        return false;
      }
      // Verification du code de livraison communique par le client au livreur.
      if (status === 'DELIVERED' && currentOrder.deliveryCode) {
        const expectedDelivery = normalizeCode(currentOrder.deliveryCode);
        const providedDelivery = normalizeCode(patch && patch.providedDeliveryCode);
        if (!providedDelivery || providedDelivery !== expectedDelivery) {
          dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Code de livraison incorrect. Demandez le code 4 chiffres au client.' });
          return false;
        }
      }
      if (currentOrder.courierId && order.courierId && currentOrder.courierId !== order.courierId) {
        dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Cette commande est assignee a un autre livreur.' });
        return false;
      }
    }
    const restaurantPreparationStatuses = ['PREPARING', 'READY_FOR_PICKUP'];
    if (restaurantPreparationStatuses.includes(status)) {
      const snapshot = await get(eatOrderRef(order.id));
      currentOrder = snapshot && snapshot.exists() ? snapshot.val() : order;
      const hasAcceptedCourier = Boolean(
        currentOrder &&
        currentOrder.courierId &&
        (currentOrder.courierStatus === 'ACCEPTED' || currentOrder.dispatchStatus === 'ACCEPTED' || currentOrder.acceptedAt)
      );
      if (!hasAcceptedCourier) {
        dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Attendez qu’un livreur accepte avant de valider la préparation côté restaurant.' });
        return false;
      }
      if (eatCourierRef && currentOrder.courierId) {
        const courierSnapshot = await get(eatCourierRef(currentOrder.courierId));
        const courier = courierSnapshot && courierSnapshot.exists() ? courierSnapshot.val() : null;
        const courierPoint = getLocationPoint(courier);
        const courierAvailable = Boolean(courier && courier.active !== false && courier.status !== 'OFFLINE' && courierPoint);
        if (!courierAvailable) {
          dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Le livreur n’est plus disponible ou sa position est manquante. Ne lancez pas la préparation.' });
          return false;
        }
      }
    }
    const deliveredPatch = status === 'DELIVERED' ? {
      completedAt: patch.completedAt || now,
      completionStatus: 'COMPLETE',
      ratingPending: patch.ratingSubmitted !== true,
      paymentCompletedAt: patch.paymentCompletedAt || now
    } : {};
    // Annulation : on libere le livreur et on coupe l'assignation pour eviter
    // qu'AppCommon (mobile) detecte la commande comme une nouvelle offre en attente
    // (commande "fantome" qui declenche la sonnerie sans course active).
    const cancelledPatch = status === 'CANCELLED' ? {
      cancelledAt: patch.cancelledAt || now,
      previousCourierId: currentOrder.courierId || null,
      courierId: null,
      courierStatus: null,
      dispatchStatus: null,
      acceptedAt: null
    } : {};
    // Si le livreur force PICKED_UP alors que le restaurant n a pas marqué les étapes
    // intermédiaires (PREPARING / READY_FOR_PICKUP), on les pose maintenant pour que
    // le timeline reste cohérent côté client/restaurant/admin.
    const backfillTimeline = {};
    if (status === 'PICKED_UP') {
      if (!currentOrder.preparingAt) backfillTimeline.preparingAt = now;
      if (!currentOrder.readyForPickupAt) backfillTimeline.readyForPickupAt = now;
    }
    // Champs transient utilises uniquement pour la validation: ne pas persister.
    const { providedPickupCode: _ppc, providedDeliveryCode: _pdc, ...patchForPersist } = (patch || {});
    const updateData = { ...patchForPersist, ...buildEatOrderTimelinePatch(status, now), ...backfillTimeline, ...deliveredPatch, ...cancelledPatch, status, updatedAt: now };
    // Defense-in-depth : Firebase RTDB rejette toute valeur `undefined` (et toute
    // sous-cle undefined dans un objet imbrique). On nettoie recursivement avant
    // l'ecriture pour eviter de faire echouer un retrait/livraison si un appelant
    // a transmis un objet partiel (ex. courierLocation sans `add`).
    const stripUndefined = (input) => {
      if (Array.isArray(input)) return input.map(stripUndefined).filter(v => v !== undefined);
      if (input && typeof input === 'object') {
        const out = {};
        Object.keys(input).forEach(key => {
          const cleaned = stripUndefined(input[key]);
          if (cleaned !== undefined) out[key] = cleaned;
        });
        return out;
      }
      return input === undefined ? undefined : input;
    };
    const safeUpdateData = stripUndefined(updateData);
    await update(eatOrderRef(order.id), safeUpdateData);
    const mirrorUpdates = [];
    const mirrorOrder = { ...currentOrder, ...order };
    if (mirrorOrder.customerId && eatCustomerOrderRef) mirrorUpdates.push(update(eatCustomerOrderRef(mirrorOrder.customerId, order.id), safeUpdateData));
    if (mirrorOrder.restaurantId && eatRestaurantOrderRef) mirrorUpdates.push(update(eatRestaurantOrderRef(mirrorOrder.restaurantId, order.id), safeUpdateData));
    if (mirrorOrder.courierId && eatCourierOrderRef) mirrorUpdates.push(update(eatCourierOrderRef(mirrorOrder.courierId, order.id), safeUpdateData));
    if (mirrorUpdates.length) await Promise.allSettled(mirrorUpdates);

    if (status === 'DELIVERED') {
      const latestSnapshot = await get(eatOrderRef(order.id));
      const deliveredOrder = latestSnapshot && latestSnapshot.exists() ? latestSnapshot.val() : { ...order, ...updateData };
      const courierId = deliveredOrder.courierId;
      const courierEarning = toNumber(deliveredOrder.pricing && deliveredOrder.pricing.courierEarning ? deliveredOrder.pricing.courierEarning : deliveredOrder.courierEarning, 0);
      if (courierId && courierEarning > 0 && !deliveredOrder.courierWalletSettledAt && singleUserRef && walletHistoryRef) {
        const settledAt = Date.now();
        const transactionResult = await runTransaction(singleUserRef(courierId), (user) => {
          if (!user) return user;
          const currentBalance = toNumber(user.walletBalance, 0);
          return { ...user, walletBalance: Number((currentBalance + courierEarning).toFixed(2)) };
        });
        if (transactionResult.committed) {
          await push(walletHistoryRef(courierId), {
            type: 'Credit',
            amount: Number(courierEarning.toFixed(2)),
            date: settledAt,
            txRef: order.id,
            service: 'eat',
            description: `Gain livraison ${deliveredOrder.reference || order.id}`
          });
          await emitWalletTransferNotification(
            courierId,
            'in',
            courierEarning,
            `Gain livraison ${deliveredOrder.reference || order.id}`,
            { orderId: order.id, service: 'eat' }
          );
          await update(eatOrderRef(order.id), {
            courierWalletSettledAt: settledAt,
            courierWalletSettlementAmount: Number(courierEarning.toFixed(2))
          });
          updateData.courierWalletSettledAt = settledAt;
          updateData.courierWalletSettlementAmount = Number(courierEarning.toFixed(2));
        }
      }
      if (courierId && eatCourierRef) {
        await update(eatCourierRef(courierId), {
          busy: false,
          available: true,
          status: 'ONLINE',
          lastCompletedOrderId: order.id,
          lastCompletedAt: Date.now(),
          updatedAt: Date.now()
        });
      }
    }

    // Annulation : si la commande etait pre-assignee a un livreur (mais pas encore
    // acceptee/recuperee), on libere ce livreur pour qu'il redevienne disponible
    // et on supprime l'entree du miroir per-courier afin que la commande "fantome"
    // ne reste pas dans son historique avec un statut ASSIGNED.
    if (status === 'CANCELLED') {
      const previousCourierId = currentOrder.courierId;
      if (previousCourierId && eatCourierRef) {
        try {
          await update(eatCourierRef(previousCourierId), {
            busy: false,
            available: true,
            status: 'ONLINE',
            currentOrderId: null,
            currentOfferId: null,
            updatedAt: Date.now()
          });
        } catch (freeError) {
          // Non bloquant
        }
      }
      // Retirer l'entree du miroir courier pour eviter ghost orders
      if (previousCourierId && eatCourierOrderRef) {
        try {
          await remove(eatCourierOrderRef(previousCourierId, order.id));
        } catch (removeError) {
          // Non bloquant
        }
      }
    }

    dispatch({ type: UPDATE_EAT_ORDER_SUCCESS, payload: { ...order, ...updateData } });
    return true;
  } catch (error) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: error.message || String(error) });
    return false;
  }
};

export const acceptEatDeliveryOrder = (order, courier = {}) => async (dispatch) => {
  const {
    eatOrderRef,
    eatCourierRef
  } = firebase;

  if (!order || !order.id || !eatOrderRef || !(courier.uid || courier.id)) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Invalid Eat delivery acceptance' });
    return false;
  }

  dispatch({ type: UPDATE_EAT_ORDER, payload: { order, status: order.status, patch: courier } });

  try {
    const now = Date.now();
    const courierId = courier.uid || courier.id;
    const courierPoint = getLocationPoint(courier);
    const restaurantPoint = getLocationPoint(order.restaurantLocation || order.restaurant);
    const deliveryDistance = getOrderDeliveryDistance(order) || toNumber(order.delivery && order.delivery.distanceKm ? order.delivery.distanceKm : order.pricing && order.pricing.deliveryDistanceKm, 0);
    const pickupDistance = courierPoint && restaurantPoint ? GetDistance(courierPoint.lat, courierPoint.lng, restaurantPoint.lat, restaurantPoint.lng) : 0;
    const { maxDeliveryDistanceKm, maxTotalRouteKm } = getEatDeliveryLimits({}, courier, order);
    if (deliveryDistance > maxDeliveryDistanceKm || pickupDistance + deliveryDistance > maxTotalRouteKm) {
      dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Livraison trop éloignée pour ce livreur.' });
      return false;
    }
    let acceptedOrder = null;

    const result = await runTransaction(eatOrderRef(order.id), currentOrder => {
      if (!currentOrder || ['PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(currentOrder.status)) return currentOrder;
      if (currentOrder.courierId && currentOrder.courierId !== courierId) return currentOrder;

      acceptedOrder = {
        ...currentOrder,
        courierId,
        courierName: getCourierName(courier),
        courierPhone: courier.mobile || courier.phone || currentOrder.courierPhone || '',
        courierRating: courier.rating || currentOrder.courierRating || null,
        courierPickupDistance: Number(pickupDistance.toFixed(2)),
        courierDeliveryDistance: Number(deliveryDistance.toFixed(2)),
        courierTotalRouteDistance: Number((pickupDistance + deliveryDistance).toFixed(2)),
        courierLocation: courierPoint || currentOrder.courierLocation || null,
        courierStatus: 'ACCEPTED',
        dispatchStatus: 'ACCEPTED',
        acceptedAt: now,
        updatedAt: now
      };
      return acceptedOrder;
    });

    if (!result.committed || !acceptedOrder || acceptedOrder.courierId !== courierId) {
      dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Commande deja prise par un autre livreur.' });
      return false;
    }

    if (eatCourierRef) {
      const courierUpdate = {
        active: true,
        available: false,
        busy: true,
        currentOfferId: null,
        status: 'BUSY',
        currentOrderId: order.id,
        updatedAt: now
      };
      if (courierPoint) {
        courierUpdate.location = courierPoint;
        courierUpdate.lat = courierPoint.lat;
        courierUpdate.lng = courierPoint.lng;
        courierUpdate.lastLocationUpdate = now;
      }
      await update(eatCourierRef(courierId), courierUpdate);
    }

    dispatch({ type: UPDATE_EAT_ORDER_SUCCESS, payload: acceptedOrder });
    return true;
  } catch (error) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: error.message || String(error) });
    return false;
  }
};

// Annulation livreur (parité VTC) : 3 min gratuites après acceptation de la
// mission, puis pénalité de 10% du gain livreur prévu. Le calcul ici sert
// uniquement à l'affichage côté app — le débit réel est fait côté serveur
// (Cloud Function onEatOrderStatusUpdated) dès que la mission est libérée.
export const EAT_CANCELLATION_GRACE_MS = 3 * 60 * 1000;
export const EAT_CANCELLATION_FEE_RATE = 0.10;

export const getEatCancellationFee = (order) => {
  if (!order || !order.acceptedAt) return 0;
  const elapsed = Date.now() - parseFloat(order.acceptedAt);
  if (elapsed <= EAT_CANCELLATION_GRACE_MS) return 0;
  const courierEarning = parseFloat(
    (order.pricing && order.pricing.courierEarning) || order.courierEarning || 0
  );
  if (!(courierEarning > 0)) return 0;
  return parseFloat((courierEarning * EAT_CANCELLATION_FEE_RATE).toFixed(2));
};

export const cancelEatDeliveryOrder = (order, courier = {}) => async (dispatch) => {
  const {
    eatOrderRef,
    eatCourierRef,
    eatSettingsRef
  } = firebase;

  const courierId = courier.uid || courier.id;
  if (!order || !order.id || !courierId || !eatOrderRef) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Invalid Eat delivery cancellation' });
    return false;
  }

  dispatch({ type: UPDATE_EAT_ORDER, payload: { order, status: order.status, patch: { courierStatus: 'CANCELLED' } } });

  try {
    const now = Date.now();
    const snapshot = await get(eatOrderRef(order.id));
    const currentOrder = snapshot && snapshot.exists() ? snapshot.val() : order;
    if (!currentOrder || ['PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(currentOrder.status)) {
      dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Annulation livreur impossible après retrait au restaurant.' });
      return false;
    }
    if (currentOrder.courierId && currentOrder.courierId !== courierId) {
      dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Cette livraison est déjà assignée à un autre livreur.' });
      return false;
    }

    const settingsSnapshot = eatSettingsRef ? await get(eatSettingsRef) : null;
    const eatSettings = settingsSnapshot && settingsSnapshot.exists() ? settingsSnapshot.val() : {};
    const replacementCourier = await findBestEatCourier(currentOrder, eatSettings, [courierId]);
    const cancelledCourierIds = { ...(currentOrder.cancelledCourierIds || {}), [courierId]: now };
    const basePatch = {
      cancelledCourierIds,
      previousCourierId: courierId,
      previousCourierCancelledAt: now,
      previousCourierCancellationReason: 'COURIER_CANCELLED_BEFORE_PICKUP',
      updatedAt: now
    };
    const assignmentPatch = replacementCourier ? {
      courierId: replacementCourier.uid || replacementCourier.id,
      courierName: getCourierName(replacementCourier),
      courierPhone: replacementCourier.mobile || replacementCourier.phone || '',
      courierRating: replacementCourier.rating || null,
      courierPickupDistance: replacementCourier.pickupDistance,
      courierDeliveryDistance: replacementCourier.deliveryDistance,
      courierTotalRouteDistance: replacementCourier.totalRouteDistance,
      maxCourierDistanceKm: replacementCourier.maxDeliveryDistanceKm,
      maxCourierRouteKm: replacementCourier.maxTotalRouteKm,
      courierStatus: 'ASSIGNED',
      dispatchStatus: 'ASSIGNED',
      assignedAt: now,
      dispatchMode: 'AUTO_REASSIGN_AFTER_COURIER_CANCEL'
    } : {
      courierId: null,
      courierName: null,
      courierPhone: null,
      courierRating: null,
      courierPickupDistance: null,
      courierDeliveryDistance: null,
      courierTotalRouteDistance: null,
      courierStatus: 'SEARCHING',
      dispatchStatus: 'SEARCHING',
      dispatchMode: 'AUTO_REASSIGN_WAITING_COURIER'
    };

    await update(eatOrderRef(order.id), { ...basePatch, ...assignmentPatch });

    if (eatCourierRef) {
      await update(eatCourierRef(courierId), {
        busy: false,
        available: true,
        currentOrderId: null,
        currentOfferId: null,
        currentRestaurantId: null,
        currentOrderReference: null,
        status: 'ONLINE',
        lastCancelledOrderId: order.id,
        lastCancelledAt: now,
        updatedAt: now
      });
      if (replacementCourier) {
        const replacementId = replacementCourier.uid || replacementCourier.id;
        await update(eatCourierRef(replacementId), {
          busy: false,
          available: true,
          currentOrderId: null,
          currentOfferId: order.id,
          currentRestaurantId: currentOrder.restaurantId || null,
          currentOrderReference: currentOrder.reference || order.id,
          status: 'ASSIGNED',
          lastAssignedAt: now,
          updatedAt: now
        });
      }
    }

    dispatch({ type: UPDATE_EAT_ORDER_SUCCESS, payload: { ...currentOrder, ...basePatch, ...assignmentPatch } });
    return true;
  } catch (error) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: error.message || String(error) });
    return false;
  }
};

const updateEatRatingAggregate = async (ratingsRef, targetRef) => {
  if (!ratingsRef || !targetRef) return;

  const snapshot = await get(ratingsRef);
  const ratings = snapshot.val() || {};
  const values = Object.values(ratings)
    .map(item => Number(item.rate || item.rating || 0))
    .filter(value => value > 0);

  if (!values.length) return;

  const average = values.reduce((total, value) => total + value, 0) / values.length;
  await update(targetRef, {
    rating: Number(average.toFixed(1)),
    ratingCount: values.length,
    updatedAt: Date.now()
  });
};

const saveEatRating = async (ratingsRef, key, ratingData) => {
  if (!ratingsRef || !key) return;
  await update(ratingsRef, { [key]: ratingData });
};

const normalizeQuestionRatings = (ratings = {}) => {
  return Object.keys(ratings || {}).reduce((values, key) => {
    const value = Number(ratings[key]);
    if (Number.isFinite(value) && value > 0) {
      values[key] = Math.min(5, Math.max(1, Number(value.toFixed(1))));
    }
    return values;
  }, {});
};

const getQuestionAverage = (ratings = {}, fallback = 0) => {
  const values = Object.values(normalizeQuestionRatings(ratings));
  if (!values.length) return Number(fallback || 0);
  return Number((values.reduce((total, value) => total + value, 0) / values.length).toFixed(1));
};

export const submitEatOrderRating = (order, ratingData = {}) => async (dispatch) => {
  const {
    eatOrderRef,
    eatCustomerOrderRef,
    eatRestaurantOrderRef,
    eatCourierOrderRef,
    eatRestaurantRatingsRef,
    eatCourierRatingsRef,
    eatRestaurantRef,
    eatCourierRef
  } = firebase;

  if (!order || !order.id || !eatOrderRef) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'Invalid Eat order rating' });
    return false;
  }

  dispatch({ type: UPDATE_EAT_ORDER, payload: { order, status: order.status, patch: ratingData } });

  try {
    const now = Date.now();
    const snapshot = await get(eatOrderRef(order.id));
    const currentOrder = snapshot && snapshot.exists() ? { ...snapshot.val(), id: order.id } : order;
    if (!currentOrder || currentOrder.status !== 'DELIVERED') {
      dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: 'La commande doit etre livree avant la notation.' });
      return false;
    }
    // Idempotence : si la note a déjà été enregistrée, on ne re-écrit pas (évite les doublons en cas de retry réseau).
    if (currentOrder.ratingSubmitted === true && currentOrder.ratedAt) {
      dispatch({ type: UPDATE_EAT_ORDER_SUCCESS, payload: currentOrder });
      return true;
    }
    const restaurantRatings = normalizeQuestionRatings(ratingData.restaurantRatings);
    const courierRatings = normalizeQuestionRatings(ratingData.courierRatings);
    const restaurantRating = getQuestionAverage(restaurantRatings, ratingData.restaurantRating);
    const courierRating = getQuestionAverage(courierRatings, ratingData.courierRating);
    const updateData = {
      restaurantRating,
      courierRating,
      restaurantRatings,
      courierRatings,
      restaurantQuestions: ratingData.restaurantQuestions || [],
      courierQuestions: ratingData.courierQuestions || [],
      restaurantFeedback: ratingData.restaurantFeedback || '',
      courierFeedback: ratingData.courierFeedback || '',
      rating: restaurantRating,
      ratingSubmitted: true,
      ratedAt: now,
      updatedAt: now
    };

    await update(eatOrderRef(order.id), updateData);

    const mirrorUpdates = [];
    if (currentOrder.customerId && eatCustomerOrderRef) mirrorUpdates.push(update(eatCustomerOrderRef(currentOrder.customerId, order.id), updateData));
    if (currentOrder.restaurantId && eatRestaurantOrderRef) mirrorUpdates.push(update(eatRestaurantOrderRef(currentOrder.restaurantId, order.id), updateData));
    if (currentOrder.courierId && eatCourierOrderRef) mirrorUpdates.push(update(eatCourierOrderRef(currentOrder.courierId, order.id), updateData));
    if (mirrorUpdates.length) await Promise.allSettled(mirrorUpdates);

    if (restaurantRating > 0 && currentOrder.restaurantId && eatRestaurantRatingsRef) {
      const restaurantRatingRef = eatRestaurantRatingsRef(currentOrder.restaurantId);
      await saveEatRating(restaurantRatingRef, order.id, {
        orderId: order.id,
        customerId: currentOrder.customerId || null,
        customerName: currentOrder.customerName || '',
        courierId: currentOrder.courierId || null,
        rate: restaurantRating,
        ratings: restaurantRatings,
        questions: updateData.restaurantQuestions,
        feedback: updateData.restaurantFeedback,
        createdAt: now
      });
      await updateEatRatingAggregate(restaurantRatingRef, eatRestaurantRef && eatRestaurantRef(currentOrder.restaurantId));
    }

    if (courierRating > 0 && currentOrder.courierId && eatCourierRatingsRef) {
      const courierRatingRef = eatCourierRatingsRef(currentOrder.courierId);
      await saveEatRating(courierRatingRef, order.id, {
        orderId: order.id,
        customerId: currentOrder.customerId || null,
        customerName: currentOrder.customerName || '',
        restaurantId: currentOrder.restaurantId || null,
        restaurantName: currentOrder.restaurantName || '',
        rate: courierRating,
        ratings: courierRatings,
        questions: updateData.courierQuestions,
        feedback: updateData.courierFeedback,
        createdAt: now
      });
      await updateEatRatingAggregate(courierRatingRef, eatCourierRef && eatCourierRef(currentOrder.courierId));
    }

    dispatch({ type: UPDATE_EAT_ORDER_SUCCESS, payload: { ...currentOrder, ...updateData } });
    return true;
  } catch (error) {
    dispatch({ type: UPDATE_EAT_ORDER_FAILED, payload: error.message || String(error) });
    return false;
  }
};

export const updateEatCourier = (courierId, patch = {}) => async (dispatch) => {
  const { eatCourierRef, eatCourierLocationRef, singleUserRef, userLocationRef } = firebase;
  const { silent, skipUserSync, ...cleanPatch } = patch;

  if (!courierId || !eatCourierRef) {
    if (!silent) dispatch({ type: UPDATE_EAT_COURIER_FAILED, payload: 'Invalid Eat courier' });
    return;
  }

  if (!silent) dispatch({ type: UPDATE_EAT_COURIER, payload: { courierId, patch: cleanPatch } });

  try {
    const now = Date.now();
    const updateData = {
      ...cleanPatch,
      id: courierId,
      uid: cleanPatch.uid || courierId,
      updatedAt: now
    };
    const location = getLocationPoint(updateData);
    if (location && updateData.active !== false && updateData.status !== 'OFFLINE') {
      updateData.locationSyncedFrom = 'eatCourier';
      updateData.locationSyncUpdatedAt = null;
    }
    await update(eatCourierRef(courierId), updateData);
    if (location && updateData.active !== false && updateData.status !== 'OFFLINE') {
      const locationUpdate = {
        lat: location.lat,
        lng: location.lng,
        location,
        lastLocationUpdate: updateData.lastLocationUpdate || now,
        locationUpdatedAt: updateData.locationUpdatedAt || updateData.lastLocationUpdate || now,
        updatedAt: now
      };
      if (userLocationRef) await update(userLocationRef(courierId), locationUpdate);
      if (eatCourierLocationRef) await update(eatCourierLocationRef(courierId), { ...updateData, ...locationUpdate });
    }
    if (!skipUserSync && singleUserRef && (cleanPatch.applicationStatus || cleanPatch.approved !== undefined || cleanPatch.active !== undefined || cleanPatch.available !== undefined || cleanPatch.siret !== undefined || cleanPatch.nafCode !== undefined || cleanPatch.idImageUrl !== undefined || cleanPatch.idImageBackUrl !== undefined || cleanPatch.rcProUrl !== undefined || cleanPatch.bankName !== undefined || cleanPatch.bankCode !== undefined || cleanPatch.bankAccount !== undefined || cleanPatch.thermalBagStatus !== undefined || cleanPatch.transportMode !== undefined)) {
      const userPatch = {
        'serviceCapabilities/eatCourier': true
      };
      if (cleanPatch.approved !== undefined) userPatch.eatCourierApproved = Boolean(updateData.approved);
      if (cleanPatch.active !== undefined || cleanPatch.available !== undefined) userPatch.eatCourierActive = Boolean(updateData.active !== undefined ? updateData.active : updateData.available);
      if (cleanPatch.transportMode !== undefined) userPatch.eatTransportMode = updateData.transportMode || 'bike';
      if (cleanPatch.thermalBagStatus !== undefined) userPatch.eatThermalBagStatus = updateData.thermalBagStatus || 'to_deliver';
      if (cleanPatch.applicationStatus || updateData.applicationStatus) userPatch.eatCourierApplicationStatus = cleanPatch.applicationStatus || updateData.applicationStatus;
      if (cleanPatch.siret !== undefined) userPatch.eatSiret = cleanPatch.siret;
      if (cleanPatch.nafCode !== undefined) userPatch.eatNafCode = cleanPatch.nafCode;
      if (cleanPatch.idImageUrl !== undefined) userPatch.idImage = cleanPatch.idImageUrl;
      if (cleanPatch.idImageBackUrl !== undefined) userPatch.idImageBack = cleanPatch.idImageBackUrl;
      if (cleanPatch.rcProUrl !== undefined) {
        userPatch.eatRcPro = cleanPatch.rcProUrl;
        userPatch.rcPro = cleanPatch.rcProUrl;
      }
      if (cleanPatch.bankName !== undefined) userPatch.bankName = cleanPatch.bankName;
      if (cleanPatch.bankCode !== undefined) userPatch.bankCode = cleanPatch.bankCode;
      if (cleanPatch.bankAccount !== undefined) userPatch.bankAccount = cleanPatch.bankAccount;
      await update(singleUserRef(courierId), userPatch);
    }
    if (!silent) dispatch({ type: UPDATE_EAT_COURIER_SUCCESS, payload: updateData });
  } catch (error) {
    if (!silent) dispatch({ type: UPDATE_EAT_COURIER_FAILED, payload: error.message || String(error) });
  }
};

export const clearEatMessages = () => ({
  type: CLEAR_EAT_MESSAGES,
  payload: null
});
